import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import type {
  AvailableAssignee,
  CreateTodoListInput,
  ItemStatus,
  Occurrence,
  OccurrenceItem,
  PropertyRole,
  TodoList,
  UpdateTodoListInput,
} from '@/types/admin/todoLists';

/**
 * RTK Query slice for the Admin To Do's feature. Mirrors sentimentApi's pattern:
 * `createApi` + `baseQueryWithReauth`, url style `api/todo-lists/...` with no leading slash.
 *
 * Every list/get/create/update endpoint's backend wraps its payload in
 * `{ success, data }`; `transformResponse` unwraps `data`. `archive` returns `{ success }` only.
 *
 * Tags (R-I, audit F28) are scoped per-list — NEVER the bare `'TodoList'` string:
 *   - `getTodoLists` provides each list's `{type:'TodoList', id}` PLUS `{type:'TodoList',
 *     id:'LIST'}` (the index sentinel). `scope=mine` and `scope=all` share this tag space, so a
 *     mutation invalidating the sentinel refetches whichever scope is currently mounted.
 *   - `getTodoList` provides only its `{type:'TodoList', id}`.
 *   - `setOccurrenceItemStatus` optimistically patches the cache and invalidates ONLY
 *     `{type:'TodoList', id: listId}`.
 *   - create/archive/update invalidate the `'LIST'` sentinel (+ the specific id where one
 *     exists), so the index refetches but sibling per-list caches are untouched.
 */

/** The `id` used for the list-index sentinel tag. */
const LIST_TAG_ID = 'LIST' as const;

/** Backend success envelope for endpoints that return a payload. */
interface DataEnvelope<T> {
  success: boolean;
  data: T;
}

export const todoListsApi = createApi({
  reducerPath: 'todoListsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['TodoList'],
  endpoints: builder => ({
    // R-A: propertyId (and scope) are required query params — the server has no lease-derived
    // property default, so the client must supply it. `scope` is caller-chosen (My/All toggle on
    // the index), staff-gated server-side. GET api/todo-lists?propertyId=&scope=
    getTodoLists: builder.query<
      TodoList[],
      { propertyId: number; scope?: 'mine' | 'all' }
    >({
      query: ({ propertyId, scope = 'mine' }) =>
        `api/todo-lists?propertyId=${propertyId}&scope=${scope}`,
      transformResponse: (response: DataEnvelope<TodoList[]>) => response.data,
      // R-I: per-list tags + a LIST sentinel; never a bare 'TodoList'.
      providesTags: result =>
        result
          ? [
              ...result.map(list => ({
                type: 'TodoList' as const,
                id: list.id,
              })),
              { type: 'TodoList' as const, id: LIST_TAG_ID },
            ]
          : [{ type: 'TodoList' as const, id: LIST_TAG_ID }],
    }),

    // GET api/todo-lists/:id
    getTodoList: builder.query<TodoList, number>({
      query: id => `api/todo-lists/${id}`,
      transformResponse: (response: DataEnvelope<TodoList>) => response.data,
      // R-I: provide only this list's id — the id-addressed read never touches the sentinel.
      providesTags: (_result, _error, id) => [{ type: 'TodoList', id }],
    }),

    // POST api/todo-lists (propertyId carried in the body — R-A).
    createTodoList: builder.mutation<TodoList, CreateTodoListInput>({
      query: body => ({ url: 'api/todo-lists', method: 'POST', body }),
      transformResponse: (response: DataEnvelope<TodoList>) => response.data,
      // R-I: a new list only invalidates the index sentinel.
      invalidatesTags: [{ type: 'TodoList', id: LIST_TAG_ID }],
    }),

    // PATCH api/todo-lists/:id. The body may carry `applyNow` (from the edit
    // form's toggle) so the server re-syncs the current occurrence atomically in
    // the same transaction — the edit flow no longer fires the separate
    // apply-now request below.
    updateTodoList: builder.mutation<
      TodoList,
      { listId: number } & UpdateTodoListInput
    >({
      query: ({ listId, ...body }) => ({
        url: `api/todo-lists/${listId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: DataEnvelope<TodoList>) => response.data,
      // R-I: invalidate this list's id AND the index sentinel (title/target may change the row).
      invalidatesTags: (_result, _error, { listId }) => [
        { type: 'TodoList', id: listId },
        { type: 'TodoList', id: LIST_TAG_ID },
      ],
    }),

    // PATCH api/todo-lists/occurrence-items/:itemId
    setOccurrenceItemStatus: builder.mutation<
      OccurrenceItem,
      { itemId: number; status: ItemStatus; listId: number }
    >({
      query: ({ itemId, status }) => ({
        url: `api/todo-lists/occurrence-items/${itemId}`,
        method: 'PATCH',
        body: { status },
      }),
      transformResponse: (response: DataEnvelope<OccurrenceItem>) =>
        response.data,
      // R-I: invalidate ONLY this list's id — NEVER the bare 'TodoList' or the LIST sentinel.
      invalidatesTags: (_result, _error, { listId }) => [
        { type: 'TodoList', id: listId },
      ],
      // R-I: optimistic patch — flip the cached item's status in every getTodoLists /
      // getTodoList entry that holds it, recompute the owning occurrence header, roll back on
      // error. The recompute mirrors the backend: an occurrence is `completed` iff no non-hidden
      // item remains in todo/in_progress.
      async onQueryStarted(
        { itemId, status, listId },
        { dispatch, queryFulfilled, getState },
      ) {
        const patches: { undo: () => void }[] = [];

        const recompute = (occurrence: Occurrence): void => {
          const openRemains = occurrence.items.some(
            item =>
              item.hiddenAt == null &&
              item.status !== 'done' &&
              item.status !== 'skipped',
          );
          occurrence.status = openRemains ? 'open' : 'completed';
        };

        // Patch the getTodoList(listId) entry, if cached.
        patches.push(
          dispatch(
            todoListsApi.util.updateQueryData('getTodoList', listId, draft => {
              const occurrence = draft.currentOccurrence;
              if (!occurrence) return;
              const item = occurrence.items.find(
                candidate => candidate.id === itemId,
              );
              if (!item) return;
              item.status = status;
              recompute(occurrence);
            }),
          ),
        );

        // Patch every cached getTodoLists entry (varies by propertyId/scope) that holds the list.
        const queries =
          (getState() as any)[todoListsApi.reducerPath]?.queries ?? {};
        for (const entry of Object.values(queries) as any[]) {
          if (entry?.endpointName !== 'getTodoLists') continue;
          patches.push(
            dispatch(
              todoListsApi.util.updateQueryData(
                'getTodoLists',
                entry.originalArgs,
                draft => {
                  const list = draft.find(candidate => candidate.id === listId);
                  const occurrence = list?.currentOccurrence;
                  if (!occurrence) return;
                  const item = occurrence.items.find(
                    candidate => candidate.id === itemId,
                  );
                  if (!item) return;
                  item.status = status;
                  recompute(occurrence);
                },
              ),
            ),
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
    }),

    // POST api/todo-lists/:id/archive → `{ success }` only.
    archiveTodoList: builder.mutation<void, { listId: number }>({
      query: ({ listId }) => ({
        url: `api/todo-lists/${listId}/archive`,
        method: 'POST',
      }),
      // R-I: archival moves the list out of the active index; refresh its id + the index.
      invalidatesTags: (_result, _error, { listId }) => [
        { type: 'TodoList', id: listId },
        { type: 'TodoList', id: LIST_TAG_ID },
      ],
    }),

    // R-G / F26: role rows for the XOR role-picker. GET api/todo-lists/roles?propertyId=
    getPropertyRoles: builder.query<PropertyRole[], { propertyId: number }>({
      query: ({ propertyId }) =>
        `api/todo-lists/roles?propertyId=${propertyId}`,
      transformResponse: (response: DataEnvelope<PropertyRole[]>) =>
        response.data,
    }),

    // R-G / F27: person-picker source — replaces the deleted old admin tasks
    // assignee query, hitting the surviving endpoint. admin-slim returns a `{ success, response }` envelope
    // (roles/controller.ts adminSlim → getPropertyAdmins), so unwrap `response` exactly as the
    // old endpoint did. GET api/v1/roles/admin-slim?propertyId=
    getAvailableAssignees: builder.query<
      AvailableAssignee[],
      { propertyId: number }
    >({
      query: ({ propertyId }) =>
        `api/v1/roles/admin-slim?propertyId=${propertyId}`,
      transformResponse: (response: {
        success: boolean;
        response: AvailableAssignee[];
      }) => (response.success ? response.response : []),
    }),
  }),
});

export const {
  useGetTodoListsQuery,
  useLazyGetTodoListsQuery,
  useGetTodoListQuery,
  useCreateTodoListMutation,
  useUpdateTodoListMutation,
  useSetOccurrenceItemStatusMutation,
  useArchiveTodoListMutation,
  useGetPropertyRolesQuery,
  useGetAvailableAssigneesQuery,
} = todoListsApi;
